import { useCallback, useState } from 'react';
import { useSnackbar } from 'notistack';
import EditManagedBasePolicyModal from 'src/features/basePolicy/EditManagedBasePolicyModal';
import StopManagingBasePolicyDialog from 'src/features/basePolicy/StopManagingBasePolicyDialog';
import { useTogglePortalManaged } from 'src/hooks/environmentWdacPolicy';
import { pagesText } from 'src/constants/pagesText';

// The star means two different things depending on which way it points, so each direction gets its
// own dialog: starting to manage opens the settings form, which is what commits the change, and
// stopping is immediate behind a confirmation. Both detected views share the star, so they share
// this rather than keeping two copies of it.
const usePolicyManagedAction = () => {
  const { enqueueSnackbar } = useSnackbar();
  const [managing, setManaging] = useState(null);
  const [unmanaging, setUnmanaging] = useState(null);
  const toggleManagedMutation = useTogglePortalManaged();

  const handleStopManaging = async () => {
    try {
      await toggleManagedMutation.mutateAsync({ envPolicyId: unmanaging.id });
      enqueueSnackbar('Policy is no longer a managed base policy.', {
        variant: 'success',
        autoHideDuration: 3000,
      });
      setUnmanaging(null);
    } catch (error) {
      // The api layer rejects with the backend's message as a plain string, and the business rules
      // behind unmanaging a base policy are worth showing verbatim.
      enqueueSnackbar(typeof error === 'string' && error ? error : pagesText.somethingWentWrong, {
        variant: 'error',
        autoHideDuration: 4000,
      });
    }
  };

  return {
    // Stable so the row renderers that take it can keep memoizing.
    onToggleManaged: useCallback(
      policy => (policy.isPortalManaged ? setUnmanaging : setManaging)(policy),
      [],
    ),
    managedDisabled: toggleManagedMutation.isLoading,
    managedDialogs: (
      <>
        {managing ? (
          <EditManagedBasePolicyModal basePolicy={managing} onClose={() => setManaging(null)} />
        ) : null}
        {unmanaging ? (
          <StopManagingBasePolicyDialog
            basePolicy={unmanaging}
            loading={toggleManagedMutation.isLoading}
            onCancel={() => setUnmanaging(null)}
            onConfirm={handleStopManaging}
          />
        ) : null}
      </>
    ),
  };
};

export default usePolicyManagedAction;
